import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";

interface SlotAgeGroupsEditorProps {
  value: Record<string, string[]>;
  onChange: (v: Record<string, string[]>) => void;
}

export const SlotAgeGroupsEditor = ({
  value,
  onChange,
}: SlotAgeGroupsEditorProps) => {
  const [slotStart, setSlotStart] = useState("");
  const [slotEnd, setSlotEnd] = useState("");
  const [ageGroup, setAgeGroup] = useState("");

  const timeOptions = useMemo(() => {
    const opts: string[] = [];
    for (let h = 0; h < 24; ++h) {
      for (let m = 0; m < 60; m += 30) {
        opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      }
    }
    return opts;
  }, []);

  const endOptions = useMemo(() => {
    if (!slotStart) return timeOptions;
    const [h, m] = slotStart.split(":").map(Number);
    const minMins = h * 60 + m + 30;
    return timeOptions.filter((t) => {
      const [eh, em] = t.split(":").map(Number);
      return eh * 60 + em >= minMins;
    });
  }, [slotStart, timeOptions]);

  const add = () => {
    if (!slotStart || !slotEnd || !ageGroup) return;
    const key = `${slotStart}-${slotEnd}`;
    const existing = value?.[key] || [];
    onChange({ ...(value || {}), [key]: [...existing, ageGroup] });
    setAgeGroup("");
  };

  const remove = (key: string, idx: number) => {
    const list = [...(value?.[key] || [])];
    list.splice(idx, 1);
    const next = { ...(value || {}), [key]: list };
    if (next[key].length === 0) delete next[key];
    onChange(next);
  };

  return (
    <Box sx={{ border: 1, borderColor: "divider", p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Start</InputLabel>
          <Select value={slotStart} label="Start" onChange={(e) => { setSlotStart(e.target.value as string); if (slotEnd && endOptions.indexOf(slotEnd) === -1) setSlotEnd(""); }}>
            <MenuItem value="">--:--</MenuItem>
            {timeOptions.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>End</InputLabel>
          <Select value={slotEnd} label="End" onChange={(e) => setSlotEnd(e.target.value as string)} disabled={!slotStart}>
            <MenuItem value="">--:--</MenuItem>
            {endOptions.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Age group"
          value={ageGroup}
          onChange={(e) => setAgeGroup(e.target.value)}
          fullWidth
        />
        <Button variant="outlined" onClick={add}>
          Add
        </Button>
      </Stack>
      <Stack spacing={1}>
        {Object.entries(value || {}).map(([key, groups]) => (
          <Box key={key}>
            <Typography variant="caption" fontWeight={600}>
              {key}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
              {groups.map((g, i) => (
                <Chip
                  key={`${key}-${i}`}
                  label={g}
                  onDelete={() => remove(key, i)}
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
